"use client";

import { IMQAProvider } from '@/components/imqa-provider';
import { ConfigForm } from '@/components/config-form';
import { AdvancedConfigForm } from '@/components/advanced-config-form';
import { FeatureToggles } from '@/components/feature-toggles';
import { TestButtons } from '@/components/test-buttons';
import { AdvancedTestButtons } from '@/components/advanced-test-buttons';
import { IMQAStatus } from '@/components/imqa-status';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useEffect, useState } from 'react';

interface WebViewParams {
  isWebView: boolean;
  sessionId: string;
  serviceName: string;
  serviceKey: string;
  serviceVersion: string;
  collectorUrl: string;
  serviceNamespace?: string;
  deploymentEnvironment?: string;
  isInstrumented: boolean;
  isDeactivated: boolean;
}

export function DashboardPage() {
  const [webViewParams, setWebViewParams] = useState<WebViewParams>({
    isWebView: false,
    sessionId: '',
    serviceName: '',
    serviceKey: '',
    serviceVersion: '',
    collectorUrl: '',
    isInstrumented: false,
    isDeactivated: false
  });

  useEffect(() => {
    // Function to check for WebView parameters
    const checkWebViewParams = () => {
      const win = window as unknown as {
        __imqa_session_id?: string;
        __imqa_service_name?: string;
        __imqa_service_key?: string;
        __imqa_service_version?: string;
        __imqa_collector_url?: string;
        __imqa_service_namespace?: string;
        __imqa_deployment_environment?: string;
        __imqa_shared_session?: boolean;
        __imqa_deactivated?: boolean;
        IMQA?: any;
      };
      const isWebView = typeof win.__imqa_session_id !== 'undefined';

      if (isWebView) {
        setWebViewParams({
          isWebView: true,
          sessionId: win.__imqa_session_id || '',
          serviceName: win.__imqa_service_name || '',
          serviceKey: win.__imqa_service_key || '',
          serviceVersion: win.__imqa_service_version || '',
          collectorUrl: win.__imqa_collector_url || '',
          serviceNamespace: win.__imqa_service_namespace,
          deploymentEnvironment: win.__imqa_deployment_environment,
          isInstrumented: win.__imqa_shared_session === true,
          isDeactivated: win.__imqa_deactivated === true
        });
        return true; // Found WebView params
      }
      return false;
    };

    // Check immediately
    if (checkWebViewParams()) {
      return; // Found, no need to poll
    }

    // Poll for WebView params (Android bridge might inject after page load)
    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = setInterval(() => {
      attempts++;
      if (checkWebViewParams() || attempts >= maxAttempts) {
        clearInterval(pollInterval);

        // If not WebView, check for regular web config
        if (attempts >= maxAttempts) {
          const win = window as any;
          const savedConfig = localStorage.getItem('imqaConfig');
          const advancedConfig = localStorage.getItem('imqa-advanced-config');

          let serviceNamespace: string | undefined;
          let deploymentEnvironment: string | undefined;

          if (savedConfig) {
            try {
              const config = JSON.parse(savedConfig);
              serviceNamespace = config.serviceNamespace;
            } catch (e) {
              console.error('Failed to parse imqaConfig', e);
            }
          }

          if (advancedConfig) {
            try {
              const config = JSON.parse(advancedConfig);
              deploymentEnvironment = config.deploymentEnvironment;
            } catch (e) {
              console.error('Failed to parse advanced config', e);
            }
          }

          if (win.IMQA && typeof win.IMQA === 'object') {
            const runtimeConfig = win.IMQA._config || {};
            serviceNamespace = serviceNamespace || runtimeConfig.serviceNamespace;
            deploymentEnvironment = deploymentEnvironment || runtimeConfig.deploymentEnvironment;
          }

          if (serviceNamespace || deploymentEnvironment) {
            setWebViewParams(prev => ({
              ...prev,
              serviceNamespace,
              deploymentEnvironment
            }));
          }
        }
      }
    }, 100); // Check every 100ms for up to 1 second

    return () => clearInterval(pollInterval);
  }, []);

  return (
    <IMQAProvider>
      <div className="grid gap-6">
        {webViewParams.isWebView && (
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <CardHeader>
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="secondary" className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                  WebView Detected
                </Badge>
                <Badge
                  variant={webViewParams.isInstrumented ? "default" : "secondary"}
                  className={webViewParams.isInstrumented
                    ? "bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200"
                    : "bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200"
                  }
                >
                  {webViewParams.isInstrumented ? "Instrumented" : "Not Instrumented"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 font-mono w-full text-sm">
                <div className="flex flex-wrap">
                  <span className="font-semibold">Collector URL:</span>
                  <span className="break-all">{webViewParams.collectorUrl}</span>
                </div>
                <div className="flex flex-wrap">
                  <span className="font-semibold">Session ID:</span>
                  <span className="break-all">{webViewParams.sessionId}</span>
                </div>
                <div className="flex flex-wrap">
                  <span className="font-semibold">Service Name:</span>
                  <span className="break-all">{webViewParams.serviceName}</span>
                </div>
                <div className="flex flex-wrap">
                  <span className="font-semibold">Service Key:</span>
                  <span className="break-all">{webViewParams.serviceKey}</span>
                </div>
                <div className="flex flex-wrap">
                  <span className="font-semibold">Service Version:</span>
                  <span className="break-all">{webViewParams.serviceVersion}</span>
                </div>
                {webViewParams.serviceNamespace && (
                  <div className="flex flex-wrap">
                    <span className="font-semibold">Service Namespace:</span>
                    <span className="break-all">{webViewParams.serviceNamespace}</span>
                  </div>
                )}
                {webViewParams.deploymentEnvironment && (
                  <div className="flex flex-wrap">
                    <span className="font-semibold">Deployment Environment:</span>
                    <span className="break-all">{webViewParams.deploymentEnvironment}</span>
                  </div>
                )}
                <div className="flex flex-wrap">
                  <span className="font-semibold">Web Agent deactivated:</span>
                  <span>{webViewParams.isDeactivated ? "Yes" : "No"}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!webViewParams.isWebView && (webViewParams.serviceNamespace || webViewParams.deploymentEnvironment) && (
          <Card className="bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800">
            <CardHeader>
              <CardTitle>Additional Configuration</CardTitle>
              <CardDescription>
                Service namespace and deployment environment settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 font-mono w-full text-sm">
                {webViewParams.serviceNamespace && (
                  <div className="flex flex-wrap">
                    <span className="font-semibold">Service Namespace:</span>
                    <span className="break-all ml-2">{webViewParams.serviceNamespace}</span>
                  </div>
                )}
                {webViewParams.deploymentEnvironment && (
                  <div className="flex flex-wrap">
                    <span className="font-semibold">Deployment Environment:</span>
                    <span className="break-all ml-2">{webViewParams.deploymentEnvironment}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-6 overflow-hidden">
          <Card className="w-full sm:w-2/3 min-w-0">
            <CardHeader>
              <CardTitle>IMQA Web Agent Configuration</CardTitle>
              <CardDescription>
                Configure the IMQA Web Agent settings and save them to localStorage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ConfigForm />
            </CardContent>
          </Card>

          <Card className="w-full sm:w-1/3 min-w-0 overflow-hidden">
            <CardHeader>
              <CardTitle>IMQA Status</CardTitle>
              <CardDescription>
                Current status of the IMQA Web Agent
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-hidden">
              <IMQAStatus />
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue="basic">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1">
            <TabsTrigger value="basic">Basic Config</TabsTrigger>
            <TabsTrigger value="advanced">Advanced Config</TabsTrigger>
            <TabsTrigger value="features">Feature Toggles</TabsTrigger>
            <TabsTrigger value="tests">Test Actions</TabsTrigger>
          </TabsList>
          
          <TabsContent value="basic">
            <Card>
              <CardHeader>
                <CardTitle>Basic IMQA Configuration</CardTitle>
                <CardDescription>
                  Quick setup for IMQA Web Agent with essential settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ConfigForm />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="advanced">
            <Card>
              <CardHeader>
                <CardTitle>Advanced IMQA Configuration</CardTitle>
                <CardDescription>
                  Complete configuration with all available options
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdvancedConfigForm />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="features">
            <Card>
              <CardHeader>
                <CardTitle>Feature Toggles</CardTitle>
                <CardDescription>
                  Enable or disable IMQA Web Agent features dynamically
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FeatureToggles />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="tests">
            <Tabs defaultValue="basic-tests">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="basic-tests">Basic Tests</TabsTrigger>
                <TabsTrigger value="advanced-tests">Advanced Tests</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic-tests">
                <Card>
                  <CardHeader>
                    <CardTitle>Basic Test Actions</CardTitle>
                    <CardDescription>
                      Test basic IMQA Web Agent functionality
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <TestButtons />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="advanced-tests">
                <Card>
                  <CardHeader>
                    <CardTitle>Advanced Test Actions</CardTitle>
                    <CardDescription>
                      Comprehensive testing of all IMQA Web Agent APIs and features
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <AdvancedTestButtons />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </div>
    </IMQAProvider>
  );
}